import { DownOutlined, InfoCircleFilled, UpOutlined } from '@ant-design/icons'
import { Form, FormInstance, Radio, Tooltip } from 'antd/lib'
import Flex from 'antd/lib/flex'
import { useForm } from 'antd/lib/form/Form'
// import Input from 'antd/lib/input'
import TextArea from 'antd/lib/input/TextArea'
import notification from 'antd/lib/notification'
import { ActionButton } from 'Components/common/ActionButton/ActionButton'
import { Loader } from 'Components/common/Loader/Loader'
import { ProjectFeatureFlags } from 'HTTPClients/projects.client'
import { useEffect, useMemo, useRef, useState } from 'react'
import { TransformedModelFeedback } from 'Types/CustomTask.types'
import {
    ContainerDataType,
    ContainerType,
    CustomDataType,
    PromptGenerationFormType,
} from 'Types/Tasks.types'
import { getTransformedModelFeedback, streamModelResponse } from 'Utils/customTask.utils'
import { MarkdownViewer } from '../MarkdownViewer/MarkdownViewer'
import classes from './PromptGeneration.module.less'
import { ModelFeedback } from '../ModelFeedback/ModelFeedback'
import { IPlugin } from 'Core/PluginCore/BasePlugin'
import { PlagiarismChecker } from 'Tasks/CustomTask/UI/common/PlagiarismChecker/PlagiarismChecker'
import MarkdownPreviewComponent from '../MarkdownPreviewComponent/MarkdownPreviewComponent'

export type PromptGenerationProps = {
    initialValues?: PromptGenerationFormType
    promptInstruction: string
    onPromptRevisionCreation: (
        type: 'PROMPT_REVISION'
    ) => Promise<ReadableStreamDefaultReader<Uint8Array<ArrayBufferLike>> | undefined>
    setIsGeneratedPromptValid: (val: boolean) => void
    setModelResponseExtraInfo: React.Dispatch<
        React.SetStateAction<{
            totalModelResponsesOnPromptRevision: number
            modelResponseFeedbackCode: {
                value: number
                label: string
            }[]
            modelResponse: string
        }>
    >
    setForms: (forms: { promptGenerationForm?: FormInstance<PromptGenerationFormType> }) => void
    featureFlags?: ProjectFeatureFlags['feature_flags']
    installedPlugins: IPlugin[]
    outputStartState?: CustomDataType
    onContainerUpdate?: (container: ContainerDataType<ContainerType.Custom, CustomDataType>) => void
}

export const PromptGeneration = (props: PromptGenerationProps) => {
    const {
        initialValues,
        promptInstruction,
        onPromptRevisionCreation,
        setIsGeneratedPromptValid,
        setModelResponseExtraInfo,
        setForms,
        installedPlugins,
        outputStartState,
        featureFlags,
    } = props
    const [form] = useForm<PromptGenerationFormType>()
    const [isCorrectResponse, setIsCorrectResponse] = useState<boolean>()
    const [fetchingModelResponse, setFetchingModelResponse] = useState(false)
    const [modelOutput, setModelOutput] = useState<{
        modelResponse: string | undefined
        modelFeedback: TransformedModelFeedback[]
        isStreamingCompleted: boolean
        hasError: boolean
    }>({
        modelResponse: undefined,
        modelFeedback: [],
        isStreamingCompleted: false,
        hasError: false,
    })
    const promptFormValue = Form.useWatch('prompt', form)
    const [optionsExpanded, setOptionsExpanded] = useState<boolean>(true)
    const [notificationApi, contextHolder] = notification.useNotification()
    const abortControllerRef = useRef<AbortController | null>(null)
    const [isPlagiarismCheckPassed, setIsPlagiarismCheckPassed] = useState<boolean>(true)

    useEffect(() => {
        setForms({ promptGenerationForm: form })
        if (installedPlugins.find(plugin => plugin.id === 'Plagiarism')) {
            setIsPlagiarismCheckPassed(false)
        }
        return () => {
            abortControllerRef.current?.abort()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (outputStartState?.response.main_info?.value?.prompt) {
            /*
             * In case of OTS project, we don't check the isCorrectResponse value so we set it to undefined so we can always edit the prompt.
             * But for other projects, we set it to false so that the user can edit the prompt after changing the value through the radio button.
             */
            setIsCorrectResponse(featureFlags?.ots_project ? undefined : false)
            setModelOutput({
                modelResponse:
                    outputStartState.response.main_info.value.prompt_revisions?.at(-1)
                        ?.steps_annotation,
                modelFeedback: getTransformedModelFeedback(
                    outputStartState.response.main_info.value.prompt_revisions?.at(-1)
                        ?.feedback_from_model || []
                ),
                isStreamingCompleted: true,
                hasError: false,
            })
        }
    }, [
        featureFlags?.ots_project,
        outputStartState?.response?.main_info?.value?.prompt,
        outputStartState?.response?.main_info?.value?.prompt_revisions,
    ])

    const onTestModelResponse = async () => {
        abortControllerRef.current?.abort()
        const controller = new AbortController()
        abortControllerRef.current = controller

        setModelOutput({
            modelFeedback: [],
            modelResponse: undefined,
            isStreamingCompleted: false,
            hasError: false,
        })
        setIsCorrectResponse(undefined)
        setFetchingModelResponse(true)

        const reader = (await onPromptRevisionCreation('PROMPT_REVISION')) ?? null
        setFetchingModelResponse(false)

        await streamModelResponse({
            reader,
            signal: controller.signal,
            onModelMessage: message => {
                setModelOutput(prev => ({
                    ...prev,
                    modelResponse: message,
                    isStreamingCompleted: false,
                }))
            },
            onModelFeedback: feedback => {
                setModelOutput(prev => ({ ...prev, modelFeedback: feedback }))
            },
            onFinalResponse: (feedback, submission, modelResponse) => {
                // Update local draft container with the response from backend
                props.onContainerUpdate?.(
                    submission.output_container as unknown as ContainerDataType<
                        ContainerType.Custom,
                        CustomDataType
                    >
                )

                const llmMetaData =
                    submission.output_container.data.data.response.internal_data?.llm_meta_data

                if (llmMetaData?.length) {
                    setModelResponseExtraInfo(prev => ({
                        ...prev,
                        modelResponseFeedbackCode: feedback.map(f => ({
                            value: f.feedback_result_code,
                            label: f.feedback_result_code_label,
                        })),
                        totalModelResponsesOnPromptRevision:
                            llmMetaData[llmMetaData.length - 1].response.number_of_responses,
                        modelResponse,
                    }))
                }

                setModelOutput(prev => {
                    return {
                        ...prev,
                        modelResponse:
                            prev.modelResponse === undefined
                                ? submission.output_container.data.data.response.main_info.value.prompt_revisions?.at(
                                      -1
                                  )?.steps_annotation
                                : prev.modelResponse,
                    }
                })
            },
            onError: message => {
                notificationApi.error({ message, placement: 'topRight' })
            },
            onComplete: hasError => {
                setModelOutput(prev => {
                    const next = { ...prev, isStreamingCompleted: true, hasError }
                    if (!hasError && featureFlags?.ots_project && next.modelResponse) {
                        setIsGeneratedPromptValid(true)
                    }
                    return next
                })
            },
        })
    }

    const previewResponse = useMemo(() => {
        return (
            <MarkdownViewer
                text={modelOutput.modelResponse ?? ''}
                title={'MODEL RESPONSE'}
                isTextStillGenerating={!modelOutput.isStreamingCompleted}
            />
        )
    }, [modelOutput.modelResponse, modelOutput.isStreamingCompleted])

    const promptOptionsComponent = useMemo(() => {
        if (!featureFlags?.stem_project) return null
        return (
            <Flex gap={6} vertical className={classes.formItemHeading}>
                <Flex gap={12}>
                    <div className={classes.optionsTitle}>
                        Enter options for the above question:
                    </div>
                    {optionsExpanded ? (
                        <UpOutlined onClick={() => setOptionsExpanded(false)} />
                    ) : (
                        <DownOutlined onClick={() => setOptionsExpanded(true)} />
                    )}
                </Flex>
                {optionsExpanded && (
                    <div>
                        {Array(4)
                            .fill(0)
                            .map((_, index) => (
                                <Form.Item<PromptGenerationFormType>
                                    key={index}
                                    className={classes.inputField}
                                    rules={[
                                        {
                                            required: true,
                                            message: `Option ${String.fromCharCode(
                                                65 + index
                                            )} is required`,
                                        },
                                    ]}
                                    name={['options', index]}
                                    label={
                                        <div
                                            className={classes.required}
                                        >{`Option ${String.fromCharCode(65 + index)}`}</div>
                                    }
                                >
                                    <TextArea rows={4} />
                                </Form.Item>
                            ))}
                    </div>
                )}
            </Flex>
        )
    }, [optionsExpanded, featureFlags?.stem_project])

    const disableForm = isCorrectResponse === false

    return (
        <div className={classes.promptGenerationContainer}>
            {contextHolder}
            <Flex vertical gap={16} className={classes.promptGenerationFormSection}>
                <div>{promptInstruction}</div>
                <div>
                    <Form
                        form={form}
                        layout="vertical"
                        initialValues={initialValues}
                        className={classes.inputContainer}
                        onFinish={() => {
                            onTestModelResponse()
                        }}
                        onValuesChange={() => {
                            setIsGeneratedPromptValid(false)
                        }}
                        requiredMark={false}
                        disabled={disableForm}
                    >
                        {/* <Form.Item<PromptGenerationFormType>
                            className={classes.inputField}
                            name="contextDocumentUrl"
                            label={
                                <p className={classes.inputTitle}>Link of the reference document</p>
                            }
                        >
                            <Input />
                        </Form.Item> */}
                        {/* <Form.Item<PromptGenerationFormType>
                            className={classes.inputField}
                            name="context"
                            label={<div className={classes.inputTitle}>Reference Content</div>}
                        >
                            <TextArea rows={15} />
                        </Form.Item> */}
                        <Tooltip
                            title={
                                disableForm
                                    ? 'This field is only editable when the model final answer is correct.'
                                    : ''
                            }
                        >
                            <Form.Item<PromptGenerationFormType>
                                className={classes.inputField}
                                rules={[{ required: true, message: 'Question is required' }]}
                                name="prompt"
                                label={<div className={classes.required}>Enter question</div>}
                            >
                                <TextArea rows={6} />
                            </Form.Item>
                        </Tooltip>
                        <Flex className={`${classes.previewContainer}`}>
                            <MarkdownPreviewComponent content={promptFormValue} />
                        </Flex>
                        {promptOptionsComponent}
                        {/* <Form.Item<PromptGenerationFormType>
                            className={classes.inputField}
                            rules={[
                                {
                                    required: true,
                                    message: 'External model name is required',
                                },
                            ]}
                            name="externalModelName"
                            label={<p className={classes.required}>External model name</p>}
                        >
                            <Input />
                        </Form.Item> */}
                        {/* <Form.Item<PromptGenerationFormType>
                            className={classes.inputField}
                            rules={[
                                { required: true, message: 'External model response is required' },
                            ]}
                            name="externalModelResponse"
                            label={<div className={classes.required}>External model response</div>}
                        >
                            <TextArea rows={15} />
                        </Form.Item> */}
                    </Form>
                    <PlagiarismChecker
                        installedPlugins={installedPlugins}
                        form={form}
                        onPlagiarismTestResult={(val: boolean) => setIsPlagiarismCheckPassed(val)}
                        onPlagiarismScan={() => {
                            setModelOutput({
                                modelFeedback: [],
                                modelResponse: undefined,
                                isStreamingCompleted: false,
                                hasError: false,
                            })
                        }}
                    />
                    <ActionButton
                        disabled={disableForm || !isPlagiarismCheckPassed}
                        color="primary"
                        onClick={() => form.submit()}
                        title="Test Model Response"
                        style={{ margin: '15px 0', borderRadius: '4px' }}
                    />
                </div>
            </Flex>

            {fetchingModelResponse && (
                <Loader
                    title={"Generating model response may take up to 3 minutes. Don't refresh. "}
                    width="480px"
                />
            )}
            {modelOutput.modelResponse !== undefined && (
                <Flex gap={16} vertical>
                    <div className={classes.separator} />
                    {previewResponse}
                    {modelOutput.isStreamingCompleted && !modelOutput.hasError && (
                        <Flex gap={16} vertical>
                            <div className={classes.separator} />
                            <ModelFeedback feedbacks={modelOutput.modelFeedback} />
                            {!featureFlags?.ots_project && (
                                <>
                                    <div className={classes.separator} />
                                    <Flex gap={16} vertical>
                                        <p className={classes.title}>FILL THE BELOW DETAILS</p>
                                        <div className={classes.required}>
                                            Is the final model response incorrect?
                                        </div>
                                        <Radio.Group
                                            onChange={e => {
                                                setIsCorrectResponse(e.target.value)
                                                setIsGeneratedPromptValid(e.target.value === false)
                                            }}
                                            value={isCorrectResponse}
                                        >
                                            <Radio value={false}>Yes</Radio>
                                            <Radio value={true}>No</Radio>
                                        </Radio.Group>
                                    </Flex>
                                    {isCorrectResponse && (
                                        <Flex gap={5} className={classes.tryAnotherPrompt}>
                                            <InfoCircleFilled className={classes.icon} />
                                            <div>
                                                Please go to the top prompt section and try another
                                                prompt which would most likely generate an incorrect
                                                answer
                                            </div>
                                        </Flex>
                                    )}
                                </>
                            )}
                        </Flex>
                    )}
                </Flex>
            )}
        </div>
    )
}
